import {
  type ESocialNetwork,
  type ISocialPostSuggestion,
} from '@mahawi/eshop-common/src/types';
import { Button, Card, Input, Space, Typography } from 'antd';
import React, { useCallback, useEffect, useState } from 'react';

import { SPACING } from '../../theme/tokens';

const { Text } = Typography;
const { TextArea } = Input;

function SuggestionCard({
  suggestion,
  inProcess,
  onPublish,
  onSkip,
}: {
  suggestion: ISocialPostSuggestion | null;
  inProcess: boolean;
  onPublish: (
    id: number,
    text: string,
    socialNetwork: ESocialNetwork,
    productUUID: string,
    country?: string,
  ) => void;
  onSkip: (id: number) => void;
}): React.ReactElement {
  const [editedText, setEditedText] = useState<string>('');

  useEffect((): void => {
    setEditedText(suggestion?.suggestedText ?? '');
  }, [suggestion]);

  const handlePublish = useCallback((): void => {
    if (suggestion) {
      onPublish(
        suggestion.id,
        editedText,
        suggestion.socialNetwork as ESocialNetwork,
        suggestion.productUUID,
      );
    }
  }, [suggestion, editedText, onPublish]);

  const handleSkip = useCallback((): void => {
    if (suggestion) {
      onSkip(suggestion.id);
    }
  }, [suggestion, onSkip]);

  if (!suggestion) {
    return (
      <Card loading={inProcess}>
        <Text type="secondary">No suggestion available for today.</Text>
      </Card>
    );
  }

  const isAlreadyProcessed: boolean =
    suggestion.status === 'published' || suggestion.status === 'skipped';

  return (
    <Card
      loading={inProcess}
      title={`Suggestion – product ${suggestion.productUUID}`}
      extra={<Text type="secondary">{suggestion.status}</Text>}
    >
      <Space orientation="vertical" style={{ width: '100%' }} size={SPACING.md}>
        <TextArea
          rows={6}
          value={editedText}
          onChange={(e: React.ChangeEvent<HTMLTextAreaElement>): void =>
            setEditedText(e.target.value)
          }
          disabled={isAlreadyProcessed || inProcess}
        />
        <Space size={SPACING.sm}>
          <Button
            type="primary"
            onClick={handlePublish}
            disabled={isAlreadyProcessed || inProcess || !editedText.trim()}
          >
            Publish
          </Button>
          <Button
            danger
            onClick={handleSkip}
            disabled={isAlreadyProcessed || inProcess}
          >
            Skip
          </Button>
        </Space>
      </Space>
    </Card>
  );
}

export default SuggestionCard;
